import { useEffect, useState } from "react"
import * as API from "services/api"
import { DEPARTMENT_KEY, FACILITY_KEY } from "utils/customNameHelpers"
import { errorToast } from "components/shared/toast"
import useCustomName from "./useCustomName"

const loadFacilitiesErrorMessage = (facilitiesName) => `Something went wrong.  Unable to load ${facilitiesName}.`
const loadDepartmentsErrorMessage = (departmentsName) => `Something went wrong.  Unable to load ${departmentsName}.`

const REPORTABLE_FIELD_LOCATION_ANSWER_FILTER_TEMPLATE = {
  reportableFieldId: null,
  departmentIds: [],
}

const setInactiveLabels = (records) => records.map((record) => ({ ...record, name: `${record.name}${record.active ? "" : " (inactive)"}` }))

function useReportableFieldLocationAnswerFilter() {
  // ... location answers
  const [facilities, setFacilities] = useState([])
  const [departments, setDepartments] = useState({})
  const [reportableFieldLocationAnswerFilters, setReportableFieldLocationAnswerFilters] = useState([])
  const [selectedFacilityIds, setSelectedFacilityIds] = useState([])

  const { pluralNameInSentence: facilitiesName } = useCustomName(FACILITY_KEY)
  const { pluralNameInSentence: departmentsName } = useCustomName(DEPARTMENT_KEY)

  useEffect(() => {
    const loadFacilities = async () => {
      const response = await API.getFacilities()

      if (response.ok) {
        setFacilities(setInactiveLabels(response.data))
      } else {
        errorToast(loadFacilitiesErrorMessage(facilitiesName))
      }
    }

    loadFacilities()
  }, [facilitiesName])

  useEffect(() => {
    const loadDepartments = async () => {
      const response = await API.getDepartments()

      if (response.ok) {
        const departmentsByFacility = setInactiveLabels(response.data).reduce((aggregator, department) => {
          if (aggregator[department.facilityId]) {
            aggregator[department.facilityId].push(department)
          } else {
            aggregator[department.facilityId] = [department]
          }

          return aggregator
        }, {})

        setDepartments(departmentsByFacility)
      } else {
        errorToast(loadDepartmentsErrorMessage(departmentsName))
      }
    }

    loadDepartments()
  }, [departmentsName])

  const facilityDepartments = (facilityId) => departments[facilityId] ?? []

  const setReportableFieldLocationAnswerFilterReportableFieldId = (reportableFieldId) => {
    let newReportableFieldLocationFilters = []

    if (reportableFieldId) {
      const currentReportableFieldLocationFilter = reportableFieldLocationAnswerFilters[0] ?? { ...REPORTABLE_FIELD_LOCATION_ANSWER_FILTER_TEMPLATE }
      newReportableFieldLocationFilters = [{ ...currentReportableFieldLocationFilter, reportableFieldId }]
    }

    setReportableFieldLocationAnswerFilters(newReportableFieldLocationFilters)
  }

  const updateReportableFieldLocationAnswerFiltersDepartmentIds = (departmentIds) => {
    const currentReportableFieldLocationFilter = reportableFieldLocationAnswerFilters[0]

    setReportableFieldLocationAnswerFilters(
      [
        {
          ...currentReportableFieldLocationFilter,
          departmentIds,
        },
      ],
    )
  }

  const addDepartmentIdsToReportableFieldLocationAnswerFilters = (departmentIds) => {
    const currentReportableFieldLocationFilter = reportableFieldLocationAnswerFilters[0]
    const updatedDepartmentIds = Array.from(new Set([...currentReportableFieldLocationFilter.departmentIds, ...departmentIds]))
    updateReportableFieldLocationAnswerFiltersDepartmentIds(updatedDepartmentIds)
  }

  const addAllFacilityDepartmentsToReportableFieldLocationAnswerFilters = (facilityId) => {
    addDepartmentIdsToReportableFieldLocationAnswerFilters(
      facilityDepartments(facilityId).map((department) => department.id),
    )
  }

  const removeDepartmentIdsFromReportableFieldLocationAnswerFilters = (departmentIds) => {
    const currentReportableFieldLocationFilter = reportableFieldLocationAnswerFilters[0]
    const updatedDepartmentIds = currentReportableFieldLocationFilter.departmentIds.filter((id) => !departmentIds.includes(id))
    updateReportableFieldLocationAnswerFiltersDepartmentIds(updatedDepartmentIds)
  }

  const removeAllFacilityDepartmentsFromReportableFieldLocationAnswerFilters = (facilityId) => {
    removeDepartmentIdsFromReportableFieldLocationAnswerFilters(
      facilityDepartments(facilityId).map((department) => department.id),
    )
  }

  return {
    addAllFacilityDepartmentsToReportableFieldLocationAnswerFilters,
    addDepartmentIdsToReportableFieldLocationAnswerFilters,
    departments,
    facilities,
    facilityDepartments,
    removeAllFacilityDepartmentsFromReportableFieldLocationAnswerFilters,
    removeDepartmentIdsFromReportableFieldLocationAnswerFilters,
    reportableFieldLocationAnswerFilters,
    selectedFacilityIds,
    setReportableFieldLocationAnswerFilterReportableFieldId,
    setReportableFieldLocationAnswerFilters,
    setSelectedFacilityIds,
  }
}

export default useReportableFieldLocationAnswerFilter
